/**
 * Boli Live - Auction Card Component
 *
 * eBay-style auction listing card with watchlist and seller info.
 */

import { Link } from 'react-router-dom';
import { Eye, Clock, Users, Star } from 'lucide-react';
import { useCountdown } from '@boli/core';
import type { Auction } from '@boli/types';
import { cn } from '../lib/utils';
import { WatchlistButton } from './WatchlistButton';

interface AuctionCardProps {
  auction: Auction & {
    sellerName?: string;
    sellerRating?: number;
    watcherCount?: number;
  };
  className?: string;
}

export function AuctionCard({ auction, className }: AuctionCardProps) {
  const { formatted, isEnded, isEndingSoon } = useCountdown(auction.endTimestamp);

  const currentPrice = auction.currentHighBid || auction.startingPrice;
  const isLive = auction.status === 'live' || auction.status === 'extended';

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Link to={`/auction/${auction.id}`} className="block h-full">
      <div
        className={cn(
          'group relative flex flex-col h-full rounded-lg overflow-hidden bg-card border border-border shadow-sm transition-shadow duration-200 hover:shadow-md',
          className
        )}
      >
        {/* Image/Thumbnail */}
        <div className="relative aspect-square overflow-hidden bg-secondary">
          <img
            src={auction.images?.[0] || '/placeholder-auction.jpg'}
            alt={auction.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />

          {/* Watchlist Button (top right) */}
          <div className="absolute top-2 right-2 z-10 bg-white/80 backdrop-blur-sm rounded-full shadow-sm">
            <WatchlistButton auctionId={auction.id} size="sm" />
          </div>

          {/* Live badge */}
          {isLive && (
            <div className="absolute top-2 left-2 z-10">
              <div className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm flex items-center gap-1 uppercase tracking-wider">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                Live
              </div>
            </div>
          )}

          {/* Bottom overlays */}
          <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
            {/* Viewer count */}
            {isLive && auction.viewerCount > 0 && (
              <div className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {auction.viewerCount}
              </div>
            )}

            {/* Watcher count */}
            {auction.watcherCount && auction.watcherCount > 0 && (
              <div className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ml-auto">
                <Users className="w-3 h-3" />
                {auction.watcherCount} watching
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 flex flex-col flex-1 gap-1">
          {/* Title */}
          <h3 className="font-medium text-foreground text-sm line-clamp-2 leading-snug group-hover:underline">
            {auction.title}
          </h3>

          {/* Seller Info */}
          {auction.sellerName && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className="truncate">{auction.sellerName}</span>
              {auction.sellerRating !== undefined && (
                <span className="flex items-center gap-0.5 text-amber-500">
                  <Star className="w-3 h-3 fill-current" />
                  {auction.sellerRating.toFixed(1)}
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="mt-1">
            <span className="text-lg font-bold text-foreground font-display block">
              {formatPrice(currentPrice)}
            </span>
            <span className="text-xs text-muted-foreground">
              {auction.bidCount} bid{auction.bidCount !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Timer */}
          {!isEnded && (
            <div className={cn("mt-auto pt-2 flex items-center gap-1 text-xs", isEndingSoon ? "text-red-600 font-bold" : "text-muted-foreground")}>
              <Clock className="w-3 h-3" />
              <span>{formatted} left</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// eBay-style skeleton
export function AuctionCardSkeleton() {
  return (
    <div className="rounded-lg overflow-hidden bg-card border border-border shadow-sm h-full flex flex-col">
      <div className="aspect-square bg-muted animate-pulse" />
      <div className="p-3 space-y-2 flex-1">
        <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
        <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
        <div className="mt-2 pt-2 space-y-1">
          <div className="h-6 bg-muted rounded w-1/3 animate-pulse" />
          <div className="h-3 bg-muted rounded w-1/4 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
